'use client';
import React, { useEffect, useState } from 'react';

import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
  themeQuartz,
  type GridReadyEvent,
} from 'ag-grid-enterprise';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  type SelectChangeEvent,
} from '@mui/material';

import { useReconGrid } from '../hooks/useReconGrid';
import CommentModal from '../components/CommentModal';
import DataGrid from '../components/DataGrid';
import { useGetReconAnalystsQuery, useGetReconDataQuery } from '../services/reconApiSlice';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDate } from '../utils/format-date';
import { useOktaAuth } from '@okta/okta-react';
import FiltersFront from '../components/Filters';
import type { IRow } from '../types/TableRows';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DownloadFileModal from '../components/DownloadModal';

const myTheme = themeQuartz.withParams({
  browserColorScheme: 'light',
  headerFontSize: 14,
  rowHeight: 50,
});

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(import.meta.env.VITE_AGGRID_LICENSE_KEY);

export const Home = () => {
  const { authState } = useOktaAuth();
  const [type, setType] = useState('UNMATCHED');
  const [singleDate, setSingleDate] = useState<Date | null>(new Date());
  const [openModal, setOpenModal] = useState(false);

  const email = authState?.idToken?.claims.email;
  const { data: analysts } = useGetReconAnalystsQuery('');
  const [currAnalystEmail, setCurrAnalystEmail] = useState<string | undefined>(undefined);

  const normalizedAnalysts = analysts ?? [];
  const hasAll = normalizedAnalysts.some((a) => a.email === 'all');

  const analystOptions = hasAll ? normalizedAnalysts : [{ fullName: 'All', email: 'all' }, ...normalizedAnalysts];

  const value: Date | null = singleDate;

  const formattedDate = formatDate(singleDate);

  const { data, refetch, isError, isLoading } = useGetReconDataQuery({
    type,
    reviewDate: formattedDate ?? '',
    email: currAnalystEmail!,
  });

  useEffect(() => {
    if (analysts && email) {
      const match = analysts.find((a) => a.email === email);
      setCurrAnalystEmail(match?.email ?? 'all');
    }
  }, [analysts, email]);

  const selectedAnalyst = analystOptions.find((a) => a.email === currAnalystEmail) ?? analystOptions[0];

  const {
    filters,
    colDefs,
    defaultColDef,
    rowSelection,
    applyFilters,
    gridApiRef,
    onSelectionChanged,
    getUniqueValues,
    onRowClicked,
    getRowId,
    modalOpen,
    handleCloseModal,
    handleFilterChange,
    handleSaveComment,
    comment,
    filterLabels,
    setComment,
    selectedRow,
    breakId,
    autoSizeStrategy,
  } = useReconGrid(data);

  const handleChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    applyFilters(params.api, filters);
  };

  const handleSingleDateChange = (newValue: Date | null) => {
    if (newValue) {
      setSingleDate(newValue);
    } else {
      setSingleDate(null);
    }
  };

  function getFirstEffectiveDate(data?: IRow[]): string | undefined {
    return data && data.length > 0 ? data[0].effectiveDate : undefined;
  }

  const handleButtonClick = () => {
    setOpenModal(true);
  };

  if (isError) return null;

  return (
    <Box className="mx-5 my-10">
      <div className="mb-8 flex flex-col justify-between gap-5 3xl:flex-row">
        <div className="flex flex-col gap-5 md:flex-row">
          <div className="flex flex-row gap-5 md:flex-col xl:flex-row">
            <FormControl sx={{ m: 0, minWidth: 80 }}>
              <InputLabel id="demo-simple-select-label">Type</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={type}
                label="Type"
                onChange={handleChange}
              >
                <MenuItem value="ALL">All</MenuItem>
                <MenuItem value="UNMATCHED">UNMATCHED</MenuItem>
                <MenuItem value="MATCHED">MATCHED</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }}>
              <Autocomplete
                options={analystOptions}
                getOptionLabel={(option) => option.fullName}
                value={selectedAnalyst}
                onChange={(_event, newValue) => {
                  setCurrAnalystEmail(newValue?.email ?? 'all');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Analyst"
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          </React.Fragment>
                        ),
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.email === value.email}
              />
            </FormControl>
          </div>

          <FiltersFront
            filters={filters}
            filterLabels={filterLabels}
            getUniqueValues={getUniqueValues}
            handleFilterChange={(field) => handleFilterChange(gridApiRef.current, field)}
          />
          <div className="flex flex-col gap-5 xs:flex-row md:flex-col lg:flex-row">
            <DatePicker
              label="Review Date"
              value={value}
              onChange={handleSingleDateChange}
              sx={{
                minWidth: 150,
                width: {
                  xs: 150,
                  sm: 210,
                },
              }}
            />
            <Box className="flex h-14 max-w-64 items-center gap-3 rounded-sm border border-gray-400 px-4 text-base 2lg:hidden">
              <div className="font-bold">Date as of:</div>
              {isLoading && <Skeleton height={50} width="200px" />}
              {getFirstEffectiveDate(data)}
            </Box>
          </div>
        </div>
        <div className="flex gap-5">
          <Box className="hidden h-14 max-w-64 items-center gap-3 rounded-sm border border-gray-400 px-4 text-base 2lg:flex">
            <div className="font-bold">Date as of:</div>
            {isLoading && <Skeleton height={50} width="80px" />}
            {getFirstEffectiveDate(data)}
          </Box>
          <Button
            className="whitespace-nowrap"
            variant="contained"
            tabIndex={-1}
            startIcon={<FileDownloadIcon />}
            onClick={handleButtonClick}
            disabled={isLoading}
          >
            Export to Excel
          </Button>
        </div>
      </div>

      <div className="relative">
        <DataGrid
          rowData={data}
          colDefs={colDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          onRowClicked={onRowClicked}
          getRowId={getRowId}
          autoSizeStrategy={autoSizeStrategy}
          theme={myTheme}
        />
      </div>

      {modalOpen && (
        <CommentModal
          refetch={refetch}
          open={modalOpen}
          comment={comment}
          breakId={breakId}
          selectedRow={selectedRow}
          onClose={handleCloseModal}
          onSave={handleSaveComment}
          onCommentChange={setComment}
          currentData={data}
        />
      )}

      <DownloadFileModal open={openModal} setOpen={setOpenModal} />
    </Box>
  );
};
